/**
 * usePeriodClockSync — keeps a timed board's period clock (basketball
 * quarters, football halves) on the server so every device shows it (LS-B09)
 * and so every event can carry the period and the clock (LS-B08).
 *
 * Scorer: whenever the clock starts, stops, or the period changes, the state
 *   is reported with onClockChange({ running, elapsedMs, period, periodLabel })
 *   and the period with onPeriodChange({ period, periodLabel }).
 * Viewer: the board's period and time left follow `serverClock`, ticking
 *   locally between updates.
 * A scorer on a device with no saved clock (second device, cleared storage)
 *   starts from the server clock instead of the start of the game.
 *
 * The board passes `apply({ period, timeLeft, running })` to set its state.
 */
import { useEffect, useRef } from "react";
import { clockElapsedMs, parseClockState } from "../../live/utils/liveScoreState";

/** Seconds left in a period of `lengthSec` for a clock state. */
export const timeLeftFromClock = (clockState, lengthSec, now = Date.now()) => {
  const elapsed = clockElapsedMs(clockState, now) / 1000;
  return Math.max(0, Math.ceil(lengthSec - elapsed));
};

const usePeriodClockSync = ({
  isReadOnlyMode,
  isMatchActive,
  serverClock,
  onClockChange,
  onPeriodChange,
  period,
  periodLabel,
  periodLengthSec,     // (period) => seconds; 0 / null = no clock (e.g. penalties)
  timeLeft,
  timerRunning,
  hasLocalState,       // the board restored its own saved clock on this device
  apply,
}) => {
  const timeLeftRef = useRef(timeLeft);
  timeLeftRef.current = timeLeft;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const lengthRef = useRef(periodLengthSec);
  lengthRef.current = periodLengthSec;
  const lastReportedRef = useRef(null);
  const hydratedRef = useRef(false);

  const clock = parseClockState(serverClock);
  const clockKey = clock ? `${clock.period}|${clock.running}|${clock.runningSince}|${clock.elapsedMs}` : null;

  // ── scorer: report period changes and clock start / stop ─────────────────
  useEffect(() => {
    if (isReadOnlyMode || !isMatchActive) return;
    const key = `${period}|${timerRunning}`;
    if (lastReportedRef.current === null) {
      // First render: nothing changed yet — remember it, but report the
      // period so events carry it (the clock itself is not touched).
      lastReportedRef.current = key;
      if (onPeriodChange) onPeriodChange({ period, periodLabel });
      return;
    }
    if (lastReportedRef.current === key) return;
    lastReportedRef.current = key;
    if (onPeriodChange) onPeriodChange({ period, periodLabel });
    const length = lengthRef.current?.(period) || 0;
    if (onClockChange && length > 0) {
      onClockChange({
        running: !!timerRunning,
        elapsedMs: Math.max(0, (length - (timeLeftRef.current ?? length)) * 1000),
        period,
        periodLabel,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnlyMode, isMatchActive, period, timerRunning]);

  // ── scorer on a fresh device: start from the server clock once ───────────
  useEffect(() => {
    if (isReadOnlyMode || hydratedRef.current || hasLocalState || !clock) return;
    hydratedRef.current = true;
    const length = lengthRef.current?.(clock.period) || 0;
    const left = length > 0 ? timeLeftFromClock(clock, length) : 0;
    lastReportedRef.current = `${clock.period}|${clock.running && left > 0}`;
    applyRef.current({ period: clock.period, timeLeft: left, running: clock.running && left > 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnlyMode, hasLocalState, clockKey]);

  // ── viewer: follow the server clock, ticking locally ─────────────────────
  useEffect(() => {
    if (!isReadOnlyMode || !clock) return undefined;
    const update = () => {
      const length = lengthRef.current?.(clock.period) || 0;
      const left = length > 0 ? timeLeftFromClock(clock, length) : 0;
      applyRef.current({ period: clock.period, timeLeft: left, running: clock.running && left > 0, viewer: true });
    };
    update();
    if (!clock.running) return undefined;
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnlyMode, clockKey]);
};

export default usePeriodClockSync;
